import React, {
  ChangeEvent,
  FunctionComponent,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';
import { BundleJson, TravelMode } from '../../../../types/bundleTypes';
import {
  BundleMonitorListProps,
  MonitorReducerStateType,
  Types,
  ModalState,
  MoveOrdersModal,
} from '../Monitor.types';
import {
  fetchAllMonitorBundleData,
  subscribeToPusherForBundleInfo,
  useInterval,
  updateBundlesInfoStatusList,
  updateSelectedBundleOrBundles,
  calculateEtaFromAlgorithm,
  serializeModalData,
  triggerMoveAction,
  renderTitles,
  useCombinedEffects,
  sortBundlesByStatus,
} from '../../../../utils/bundlesMonitorUtils';
import { monitorStorageReducer } from '../MonitorReducer';
import { Loader } from '../../../../components/Loader/Loader';
import './MonitorList.scss';
import ChangeShelfModal from '../../../oldPlanner/modals/ChangeShelfModal';
import ETAOptionsModal from '../../../oldPlanner/modals/ETAOptionsModal';
import SelectCourierModal from '../../../../components/modals/SelectCourierModal';
import { BundleMapWindow } from '../../../oldPlanner/planner/BundleMapWindow';
import Modal from '../../../../components/modals/Modal';
import MovingOrdersToBundleModalContent from '../../../oldPlanner/planner/MovingOrdersToBundleModalContent';
import {
  useDeepCompareCallback,
  useDeepCompareEffect,
  useDeepCompareMemo,
} from 'use-deep-compare';
import { Region } from '../../../../types/orderTypes';
import { InputComponent } from '../../../../ReusableComponents/Input/InputComponent';
import _ from 'lodash';
import { ButtonComponent } from '../../../../ReusableComponents/Button/ButtonComponent';
import { ComboBoxComponent } from '../../../../ReusableComponents/ComboBox/ComboBoxComponent';
import { ComboBoxItem } from '../../../../ReusableComponents/ComboBox/ComboBoxComponent.types';
import { filterBundlesBySearchPhrase } from './MonitorListUtils';
import { MemoizedCageInfo } from '../cageInfo/CageInfo';
import { MonitorCollapseWrapper } from '../MonitorCollapse/MonitorCollapseWrapper';
import { ChangePickupPointModal } from '../../../planner/tools/components/ChangePickupPointModal';

const initialStateReducer: MonitorReducerStateType = {
  bundles: [],
  orders: null,
  couriers: null,
  bundlesInfoStatus: null,
  loading: true,
  sortKey: '',
  sortType: '',
  autoSort: false,
};

const MonitorListWrapper: FunctionComponent<BundleMonitorListProps> = memo(
  props => {
    const { completedStatusList, region } = props;
    const [state, dispatch] = useReducer(
      monitorStorageReducer,
      initialStateReducer
    );
    const {
      bundles,
      loading,
      bundlesInfoStatus,
      orders,
      sortType,
      sortKey,
      autoSort,
      couriers,
    } = state;
    const [filteredCourierName, setFilteredCourierName] = useState<string>('');
    const [selectedRegion, setRegion] = useState<ComboBoxItem>({
      name: 'ALL',
      value: 'ALL',
    });
    const inputRef = useRef<HTMLInputElement>(null);
    const [filteredBundles, setFilteredBundles] = useState<BundleJson[]>(null);
    const [showMapModal, toggleMapModal] = useState<ModalState>({
      isOpen: false,
    });
    const [showETAModal, toggleETAModal] = useState<ModalState>({
      isOpen: false,
    });
    const [showShelfModal, toggleShelfModal] = useState<ModalState>({
      isOpen: false,
    });
    const [showCourierModal, toggleCourierModal] = useState<ModalState>({
      isOpen: false,
    });
    const [showMoveOrdersModal, toggleMoveOrdersModal] =
      useState<MoveOrdersModal>({
        isOpen: false,
      });
    const [showPickupPointModal, togglePickupPointModal] = useState<ModalState>(
      {
        isOpen: false,
      }
    );
    const [showCageInfo, toggleCageInfo] = useState<boolean>(false);
    const [selectedBundleToMoveOrdersTo, setSelectedBundleToMoveOrdersTo] =
      useState<string>(null);

    const currentBundlesId = bundles
      ? bundles.map(bundle => bundle.id).sort((a, b) => +b - +a)
      : [];
    const memoizedCurrentBundlesId = useDeepCompareMemo(() => {
      return currentBundlesId;
    }, [currentBundlesId]);

    const updateSelectedBundleOrBundlesCallback = useCallback(
      async (bundleIds: string[]) => {
        const response = await updateSelectedBundleOrBundles(bundleIds);
        const { fetchedBundles, fetchedOrders } = response;
        const filteredFetchedOrders = {};
        const filteredFetchedBundles = fetchedBundles.filter(bundle => {
          if (memoizedCurrentBundlesId.includes(bundle.id)) {
            filteredFetchedOrders[bundle.id] = fetchedOrders[bundle.id];
            return true;
          }
          return false;
        });
        if (filteredFetchedBundles.length) {
          if (bundleIds.length === 1) {
            const [bundleId] = bundleIds;
            dispatch({
              type: Types.UpdateSelectedBundle,
              payload: {
                bundleId,
                bundles: fetchedBundles,
                order: fetchedOrders[bundleId],
              },
            });
          } else {
            dispatch({
              type: Types.UpdateSelectedBundles,
              payload: {
                bundleIds,
                bundles: fetchedBundles,
                orders: fetchedOrders,
              },
            });
          }
        }
      },
      [memoizedCurrentBundlesId]
    );

    const subscribeToPusherForBundlesInfoCallback = useCallback(
      (currentBundlesId: string[]) =>
        subscribeToPusherForBundleInfo(
          dispatch,
          updateSelectedBundleOrBundlesCallback,
          currentBundlesId,
          region
        ),
      [region, updateSelectedBundleOrBundlesCallback]
    );
    const fetchAllMonitorBundleDataCallback = useCallback(async () => {
      await fetchAllMonitorBundleData(dispatch, region);
    }, [region]);

    useInterval(() => {
      const updatedBundlesInfoStatus = updateBundlesInfoStatusList(
        bundles,
        bundlesInfoStatus
      );
      const areBundlesInfoStatusEqual = _.isEqual(
        updatedBundlesInfoStatus,
        bundlesInfoStatus
      );
      if (!autoSort && !areBundlesInfoStatusEqual) {
        dispatch({
          type: Types.UpdateBundlesInfoStatus,
          payload: {
            bundlesInfoStatus: updatedBundlesInfoStatus,
          },
        });
      } else if (autoSort && !sortKey) {
        const sortedBundles = sortBundlesByStatus(
          bundles,
          updatedBundlesInfoStatus
        );
        const areSortedBundlesEqual = _.isEqual(sortedBundles, bundles);
        if (!areSortedBundlesEqual) {
          dispatch({
            type: Types.UpdateBundlesInfoStatusAndSortBundles,
            payload: {
              bundles: sortedBundles,
              bundlesInfoStatus: updatedBundlesInfoStatus,
            },
          });
        }
      }
    }, 60000);

    const toggleCollapseOrTableView = useCallback(
      (bundleId: string, type?: 'collapse' | 'table') => {
        dispatch({
          type: Types.ToggleCollapseOrTableView,
          payload: {
            bundleId,
            type: type || 'collapse',
          },
        });
      },
      []
    );

    const memoizedMonitorTitles = useMemo(() => {
      return renderTitles(
        completedStatusList,
        sortKey,
        sortType,
        dispatch,
        autoSort
      );
    }, [autoSort, completedStatusList, dispatch, sortKey, sortType]);

    useCombinedEffects(
      fetchAllMonitorBundleDataCallback,
      bundles,
      orders,
      subscribeToPusherForBundlesInfoCallback,
      memoizedCurrentBundlesId,
      region
    );

    const debouncedFilterAction = useCallback(
      _.debounce((searchPhrase: string, selectedRegionValue: string) => {
        if (searchPhrase) {
          setFilteredBundles(prevFilteredBundles => {
            const newFilteredBundles = filterBundlesBySearchPhrase(
              bundles,
              searchPhrase,
              selectedRegionValue
            );
            return _.isEqual(prevFilteredBundles, newFilteredBundles)
              ? prevFilteredBundles
              : newFilteredBundles;
          });
        } else if (selectedRegionValue !== 'ALL') {
          setFilteredBundles(() =>
            bundles.filter(bundle => selectedRegionValue === bundle.region)
          );
        } else {
          setFilteredBundles(null);
        }
      }, 500),
      [bundles]
    );

    const collapseAllBundlesCallback = useDeepCompareCallback(() => {
      const bundleIdsToClose = [];
      Object.keys(bundlesInfoStatus).forEach(bundleId => {
        if (
          bundlesInfoStatus[bundleId].collapseOpen ||
          bundlesInfoStatus[bundleId].tablePickupsOpen
        ) {
          bundleIdsToClose.push(bundleId);
        }
      });
      if (bundleIdsToClose.length) {
        dispatch({ type: Types.CollapseAll, payload: { bundleIdsToClose } });
      }
    }, [bundlesInfoStatus]);

    const onChangeInput = useCallback(
      (event: ChangeEvent<HTMLInputElement>) => {
        setFilteredCourierName(event.currentTarget.value);
        debouncedFilterAction(event.currentTarget.value, selectedRegion.value);
      },
      [debouncedFilterAction, selectedRegion.value]
    );

    const onChangeCombobox = useCallback(
      (event: ChangeEvent, value: ComboBoxItem) => {
        setRegion(value);
        debouncedFilterAction(filteredCourierName, value.value);
      },
      [debouncedFilterAction, filteredCourierName]
    );

    const searcherListener = event => {
      if (
        (event.key === 'f' || event.key === 'F') &&
        (event.ctrlKey || event.metaKey)
      ) {
        event.preventDefault();
        if (inputRef.current) {
          inputRef.current.focus();
        }
      }
    };

    useDeepCompareEffect(() => {
      if (
        ((autoSort || sortKey || sortType) && selectedRegion.value !== 'ALL') ||
        ((!autoSort || !sortKey) && selectedRegion.value !== 'ALL')
      ) {
        debouncedFilterAction(filteredCourierName, selectedRegion.value);
      }
    }, [
      debouncedFilterAction,
      filteredCourierName,
      selectedRegion,
      sortType,
      sortKey,
      autoSort,
    ]);

    useEffect(() => {
      window.addEventListener('keydown', searcherListener);
      return function clearListCache() {
        window.removeEventListener('keydown', searcherListener);
      };
    }, []);

    const regions = useMemo(() => {
      const comboBoxRegions = [{ name: 'ALL', value: 'ALL' }];
      for (const value in Region) {
        if (Region.hasOwnProperty(value)) {
          comboBoxRegions.push({ name: value, value });
        }
      }
      return comboBoxRegions;
    }, []);

    if (loading || !orders) {
      return (
        <div className="loader-wrapper">
          <Loader size="xxl" type="spinner" />
        </div>
      );
    }

    return (
      <div className="monitor-list">
        <div className="monitor-list__bundle-search">
          <InputComponent
            className="bundle-search-input"
            type="text"
            fullWidth
            variant="outlined"
            id="bundle-search"
            label="Search by courier name or phone number / shelf / bundle id"
            name="bundle-search"
            onChange={onChangeInput}
            value={filteredCourierName}
            inputRef={inputRef}
          />
          <ButtonComponent
            size="small"
            text="Collapse all bundles"
            onClick={collapseAllBundlesCallback}
          />
        </div>
        <div className="monitor-list__region-filter">
          <ComboBoxComponent
            id="region-filter"
            value={selectedRegion}
            comboBoxName="Filter by region"
            comboBoxItems={regions}
            disableClearable
            externalValuesHandler
            onChange={onChangeCombobox as any}
          />
        </div>
        <div>
          {showCageInfo && (
            <MemoizedCageInfo bundles={bundles} regions={regions} />
          )}
          <button
            className="monitor-list__cageInfo-button"
            onClick={() => toggleCageInfo(!showCageInfo)}
          >
            {showCageInfo ? 'Hide cage overview' : 'See cage overview'}
          </button>
        </div>
        <div className="monitor-list__header">{memoizedMonitorTitles}</div>
        <div className="monitor-list-collapse-group">
          {bundles.map((bundle, index) => {
            if (filteredBundles) {
              bundle = filteredBundles[index];
            }
            if (!bundlesInfoStatus[bundle?.id]) {
              return null;
            }
            const currentOrders = orders[bundle.id];
            const currentBundleInfoStatus = bundlesInfoStatus[bundle.id];
            return (
              <div key={`monitor-collapse-wrapper-${bundle.id}`}>
                <MonitorCollapseWrapper
                  region={region}
                  key={bundle.id}
                  bundleId={bundle.id}
                  bundle={bundle}
                  isBundleLoading={currentBundleInfoStatus.loading}
                  bundleDeliveryStatus={
                    currentBundleInfoStatus.bundleDeliveryStatus
                  }
                  currentBundle={bundle}
                  currentOrders={currentOrders}
                  dispatch={dispatch}
                  isBundleOpen={currentBundleInfoStatus.collapseOpen}
                  updateSelectedBundleOrBundlesCallback={
                    updateSelectedBundleOrBundlesCallback
                  }
                  expandedCallback={toggleCollapseOrTableView}
                  lastViewMode={currentBundleInfoStatus.view}
                  lastSelectedOrders={currentBundleInfoStatus.selectedOrders}
                  toggleMapModal={toggleMapModal}
                  toggleETAModal={toggleETAModal}
                  toggleShelfModal={toggleShelfModal}
                  toggleCourierModal={toggleCourierModal}
                  toggleMoveOrdersModal={toggleMoveOrdersModal}
                  togglePickupPointModal={togglePickupPointModal}
                />
              </div>
            );
          })}
        </div>
        {showShelfModal.isOpen &&
          showShelfModal.bundleId &&
          (() => {
            const { bundleId } = showShelfModal;
            const { currentBundle } = serializeModalData(
              bundleId,
              bundles,
              orders,
              couriers
            );
            return (
              <ChangeShelfModal
                bundle={currentBundle}
                onCloseModal={() => {
                  toggleShelfModal({ isOpen: false, bundleId: '' });
                }}
                onSelectModal={async () => {
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: true,
                    },
                  });
                  await updateSelectedBundleOrBundlesCallback([bundleId]);
                  toggleShelfModal({ isOpen: false, bundleId: '' });
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: false,
                    },
                  });
                }}
              />
            );
          })()}
        {showETAModal.isOpen &&
          showETAModal.bundleId &&
          (() => {
            const { bundleId } = showETAModal;
            const { courier } = serializeModalData(
              bundleId,
              bundles,
              orders,
              couriers
            );
            return (
              <ETAOptionsModal
                onCloseModal={() => {
                  toggleETAModal({ isOpen: false, bundleId: '' });
                }}
                onTravelModeSelected={async (
                  travelMode: TravelMode,
                  timePerStop: number
                ) => {
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: true,
                    },
                  });
                  calculateEtaFromAlgorithm(
                    dispatch,
                    bundleId,
                    travelMode,
                    timePerStop,
                    updateSelectedBundleOrBundlesCallback
                  );
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: false,
                    },
                  });
                  toggleETAModal({ isOpen: false, bundleId: '' });
                }}
                region={region}
                predictionModel={courier?.predictionModel}
              />
            );
          })()}
        {showCourierModal.isOpen &&
          showCourierModal.bundleId &&
          (() => {
            const { bundleId } = showCourierModal;
            const { currentBundle, courierId } = serializeModalData(
              bundleId,
              bundles,
              orders,
              couriers
            );
            return (
              <SelectCourierModal
                bundleId={bundleId}
                onCloseCourierModal={() =>
                  toggleCourierModal({ isOpen: false, bundleId: '' })
                }
                onSelectedCourier={async () => {
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: true,
                    },
                  });
                  await updateSelectedBundleOrBundlesCallback([bundleId]);
                  toggleCourierModal({ isOpen: false, bundleId: '' });
                  dispatch({
                    type: Types.SetLoadingForBundle,
                    payload: {
                      bundleId,
                      bundlesLoadingStatus: false,
                    },
                  });
                }}
                assignedCourierId={courierId}
                region={currentBundle.region}
              />
            );
          })()}
        {showMapModal.isOpen &&
          showMapModal.bundleId &&
          orders[showMapModal.bundleId].length &&
          (() => {
            const { bundleId } = showMapModal;
            const { currentOrders, currentBundle, courier } =
              serializeModalData(bundleId, bundles, orders, couriers);

            return (
              <BundleMapWindow
                region={currentBundle.region}
                bundleId={bundleId}
                orderInstructions={currentOrders}
                onClose={() => toggleMapModal({ isOpen: false, bundleId: '' })}
                onTop
                courier={courier}
                newMonitor
              />
            );
          })()}
        {showMoveOrdersModal.isOpen &&
          showMoveOrdersModal.bundleId &&
          showMoveOrdersModal.selectedOrdersToMove &&
          (() => {
            const { bundleId, selectedOrdersToMove } = showMoveOrdersModal;
            const { currentBundle } = serializeModalData(
              bundleId,
              bundles,
              orders,
              couriers
            );
            const filteredBundlesByRegion = bundles.filter(
              bundle =>
                bundle &&
                bundle.region === currentBundle.region &&
                bundle.id !== currentBundle.id
            );
            return (
              <Modal
                onClose={() => {
                  toggleMoveOrdersModal({ isOpen: false, bundleId: '' });
                  setSelectedBundleToMoveOrdersTo('');
                }}
                buttons={[
                  {
                    title: 'Select',
                    disabled: !selectedBundleToMoveOrdersTo,
                    onClick: async () => {
                      await triggerMoveAction(
                        bundleId,
                        selectedBundleToMoveOrdersTo,
                        selectedOrdersToMove,
                        toggleMoveOrdersModal,
                        setSelectedBundleToMoveOrdersTo
                      );
                    },
                  },
                ]}
              >
                <MovingOrdersToBundleModalContent
                  bundleList={filteredBundlesByRegion}
                  selectedBundle={selectedBundleToMoveOrdersTo}
                  onSelectBundle={setSelectedBundleToMoveOrdersTo}
                />
              </Modal>
            );
          })()}
        {showPickupPointModal.isOpen &&
          showPickupPointModal.bundleId &&
          (() => {
            const { currentBundle, currentOrders: currentOrderInstructions } =
              serializeModalData(
                showPickupPointModal.bundleId,
                bundles,
                orders,
                couriers
              );
            return (
              <ChangePickupPointModal
                bundleId={showPickupPointModal.bundleId}
                bundleOrderIds={currentOrderInstructions.map(
                  orderInstruction => orderInstruction.order.id
                )}
                currentPickupAddress={
                  currentOrderInstructions[0].order.originAddress
                }
                region={currentBundle.region}
                onCloseModal={async changed => {
                  if (changed) {
                    await updateSelectedBundleOrBundlesCallback([
                      showPickupPointModal.bundleId,
                    ]);
                  }
                  togglePickupPointModal({
                    isOpen: false,
                    bundleId: '',
                  });
                }}
              />
            );
          })()}
      </div>
    );
  }
);
MonitorListWrapper.displayName = 'MonitorListWrapper';
MonitorListWrapper.whyDidYouRender = true;
export { MonitorListWrapper };
